import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { SessionVariable } from '../enum/session-variable';
import { SharedSignalsService } from './shared-signals.service';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  trasferSingal = inject(SharedSignalsService);
  private translateService = inject(TranslateService);
  private storageService = inject(LocalStorageService);

  // Initialize the translation service
  init() {
    this.translateService.addLangs(['en-US', 'ar-KW']); // Add supported languages

    const storedLanguage = this.storageService.getItem(SessionVariable.LANGUAGE) as string | null;
    const currentLanguage = storedLanguage ?? 'en-US';

    if (storedLanguage == null) {
      this.storageService.setItem(SessionVariable.LANGUAGE, 'en-US');
    }

    this.trasferSingal.setTextDirection(currentLanguage === 'ar-KW' ? 'rtl' : 'ltr');
    this.translateService.setDefaultLang(currentLanguage);
    this.loadLanguage(currentLanguage).subscribe();
  }

  getCurrentLang(): string {
    return this.translateService.currentLang;
  }

  resolveLanguage(preference?: string | null): 'en-US' | 'ar-KW' {
    const normalized = preference?.toUpperCase();

    return normalized === 'AR' || normalized === 'AR-KW' ? 'ar-KW' : 'en-US';
  }

  private hasLoadedTranslations(lang: string): boolean {
    const translations = this.translateService.translations[lang];

    return !!translations && Object.keys(translations).length > 0;
  }

  private loadLanguage(lang: string): Observable<unknown> {
    if (this.hasLoadedTranslations(lang)) {
      return this.translateService.currentLang === lang
        ? of(this.translateService.translations[lang])
        : this.translateService.use(lang);
    }

    if (this.translateService.currentLang === lang) {
      return this.translateService.reloadLang(lang);
    }

    return this.translateService.use(lang);
  }

  loadTranslations(lang: string) {
    this.storageService.setItem(SessionVariable.LANGUAGE, lang);
    return this.loadLanguage(lang);
  }

  switchLanguage(lang: string): Observable<unknown> {
    this.storageService.setItem(SessionVariable.LANGUAGE, lang);
    this.trasferSingal.setTextDirection(lang === 'ar-KW' ? 'rtl' : 'ltr');
    this.translateService.setDefaultLang(lang);
    return this.loadLanguage(lang);
  }

  getLabelValue(key: string, param?: string | number | boolean | object | null) {
    if (param) {
      return this.translateService.instant(key, param);
    } else {
      return this.translateService.instant(key);
    }
  }
}
